// SORT A TABLE BY ANY HEADER, ON WHAT THE CELL SHOWS, REMEMBERED PER PERSON
// (owner, 2026-10-22/23: the shop drawings register, then "do the same sort
// on the contractor cost table"). For antd tables that hold all their rows;
// a paged table sorts on the server (see the issues list) or it sorts one
// page and calls it the list.
//
//   values   { [column key]: (row, ctx) => value }  - what the cell SHOWS, as
//            a number or text; null/undefined/"" is an empty cell.
//   sort     { key, order } | null - the person's remembered sort.
//
// AN EMPTY CELL GOES LAST, BOTH WAYS: a table sorted by date, newest first,
// opens on the newest date, not on forty rows that have none.
import { useCallback } from "react";
import { usePreference } from "helpers/userPrefs";

const isEmpty = (v) => v == null || v === "" || (typeof v === "number" && Number.isNaN(v));

export function compareValues(a, b) {
  if (typeof a === "number" && typeof b === "number") return a - b;
  // "A-102" before "A-1001": sheet numbers and labels read as numbers.
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

/** antd compare for one value getter; antd reverses it for "descend", so an
 *  empty value is flipped to stay at the bottom. */
export function sorterBy(get, ctx) {
  return (ra, rb, order) => {
    const a = get(ra, ctx);
    const b = get(rb, ctx);
    const ea = isEmpty(a);
    const eb = isEmpty(b);
    if (ea && eb) return 0;
    const last = order === "descend" ? -1 : 1;
    if (ea) return last;
    if (eb) return -last;
    return compareValues(a, b);
  };
}

/** A column's key in `values`: its `key`, else its dataIndex. */
const keyOf = (c) => String(c.key != null ? c.key : c.dataIndex);

/** Every column with a value getter gets a sorter; the rest pass through.
 *  With `sort` the columns are CONTROLLED, so the table opens sorted the way
 *  the person left it; a key no column has any more opens in the table's
 *  own order. */
export function withSorting(columns, values, sort, ctx) {
  const known = !!(sort && values[sort.key] && (sort.order === "ascend" || sort.order === "descend"));
  return columns.map((c) => {
    const get = values[keyOf(c)];
    if (!get) return c;
    const sortOrder = known && sort.key === keyOf(c) ? sort.order : null;
    // No "click to sort" tooltip: the arrows say it, and a tooltip per header
    // is one more thing to render per refresh.
    return { ...c, sorter: sorterBy(get, ctx), sortOrder, showSorterTooltip: false };
  });
}

/** [sort, onChange] - the remembered sort and the Table's onChange that
 *  saves it. A third click clears it (null). */
export function useRememberedSort(prefKey) {
  const [sort, setSort] = usePreference(prefKey, null);
  const onChange = useCallback((_p, _f, sorter, extra) => {
    if (!extra || extra.action !== "sort") return;
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    const key = s && (s.columnKey != null ? s.columnKey : s.field);
    setSort(s && s.order && key != null ? { key: String(key), order: s.order } : null);
  }, [setSort]);
  return [sort, onChange];
}
